import type { IAnatomyControlIntegrationApi, IAnatomyOrientationController } from './interfaces';
import { injectable } from 'inversify';
import { AnatomicalArrowDirection } from './AnatomyOrientationControl';
import type { IViewPortOrientation } from '@/engine/app/interfaces';
import { ViewPortOrientation } from '@/engine/app/models/ViewPortOrientation';
import * as THREE from 'three';
import * as Helpers from '@/engine/PlanningAlgorithm/Helpers';

@injectable()
export class AnatomyOrientationController implements IAnatomyOrientationController {
  private _integrationApi?: IAnatomyControlIntegrationApi;

  public setIntegrationApi(api: IAnatomyControlIntegrationApi): void {
    this._integrationApi = api;
  }

  // eslint-disable-next-line @typescript-eslint/no-misused-promises -- //TODO: [chebotarev] fix eslint no-misused-promises
  public async changeDirection(direction: AnatomicalArrowDirection): Promise<void> {
    const orientation = await this._getOrientationByDirection(direction);
    this._getIntegrationApi().updateParentOrientation(orientation.topVector, orientation.directionVector);
  }

  public async checkDirectionMatch(): Promise<AnatomicalArrowDirection | undefined> {
    const quaternion = this._getIntegrationApi().getCameraOrientation();
    const tolerance = 0.001;
    const cameraTopVector = new THREE.Vector3(0, 1, 0).applyQuaternion(quaternion);
    const cameraDirectionVector = new THREE.Vector3(0, 0, 1).applyQuaternion(quaternion);
    const matches = await Promise.all(
      Object.values(AnatomicalArrowDirection).map(async (value: AnatomicalArrowDirection) => {
        const orientation = await this._getOrientationByDirection(value);
        if (
          orientation.topVector.distanceTo(cameraTopVector) < tolerance &&
          orientation.directionVector.distanceTo(cameraDirectionVector) < tolerance
        ) {
          return value;
        } else {
          return undefined;
        }
      })
    );

    return matches.find((match) => match !== undefined);
  }

  private _getIntegrationApi(): IAnatomyControlIntegrationApi {
    if (!this._integrationApi) {
      throw new Error('Integration API is not defined"');
    }
    return this._integrationApi;
  }

  private async _getOrientationByDirection(direction: AnatomicalArrowDirection): Promise<IViewPortOrientation> {
    const frontOrientation = await this._getIntegrationApi().getModelFrontOrientation();

    const vecZ = frontOrientation.topVector;
    const vecY = frontOrientation.directionVector;
    const vecX = vecY.clone().cross(vecZ);

    const mtx = Helpers.makeTransformation(new THREE.Vector3(), vecX, vecY, vecZ);

    switch (direction) {
      case AnatomicalArrowDirection.Front:
        return new ViewPortOrientation(
          new THREE.Vector3(0, 0, 1).transformDirection(mtx),
          new THREE.Vector3(0, -1, 0).transformDirection(mtx)
        );
      case AnatomicalArrowDirection.Back:
        return new ViewPortOrientation(
          new THREE.Vector3(0, 0, 1).transformDirection(mtx),
          new THREE.Vector3(0, 1, 0).transformDirection(mtx)
        );
      case AnatomicalArrowDirection.Left:
        return new ViewPortOrientation(
          new THREE.Vector3(0, 0, 1).transformDirection(mtx),
          new THREE.Vector3(1, 0, 0).transformDirection(mtx)
        );
      case AnatomicalArrowDirection.Right:
        return new ViewPortOrientation(
          new THREE.Vector3(0, 0, 1).transformDirection(mtx),
          new THREE.Vector3(-1, 0, 0).transformDirection(mtx)
        );
      case AnatomicalArrowDirection.Top:
        return new ViewPortOrientation(
          new THREE.Vector3(0, 1, 0).transformDirection(mtx),
          new THREE.Vector3(0, 0, 1).transformDirection(mtx)
        );
      case AnatomicalArrowDirection.Bottom:
        return new ViewPortOrientation(
          new THREE.Vector3(0, -1, 0).transformDirection(mtx),
          new THREE.Vector3(0, 0, -1).transformDirection(mtx)
        );
      default:
        throw new Error('Unknown direction');
    }
  }
}
